import type { ServerWebExchange } from '../../../types/web/server';
import { MapHttpHeaders } from '../../http/headers.ts';
import type { WebFilter, WebFilterChain } from '../../web/server/types.ts';

type ServerHttpHeadersWriter = (exchange: ServerWebExchange) => Promise<void>;

const staticServerHttpHeadersWriter = (headers: MapHttpHeaders): ServerHttpHeadersWriter => {
    return async (exchange: ServerWebExchange) => {
        let containsNoHeaders = true;
        const { response } = exchange;
        for (const name of headers.keys()) {
            if (response.headers.has(name)) {
                containsNoHeaders = false;
            }
        }
        if (containsNoHeaders) {
            for (const [name, value] of headers) {
                response.headers.set(name, value);
            }
        }
    }
}

const cacheControlServerHttpHeadersWriter = () => staticServerHttpHeadersWriter(
    new MapHttpHeaders()
        .add('cache-control', 'no-cache, no-store, max-age=0, must-revalidate')
        .add('pragma', 'no-cache')
        .add('expires', '0'));

const contentTypeServerHttpHeadersWriter = () => staticServerHttpHeadersWriter(
    new MapHttpHeaders()
        .add('x-content-type-options', 'nosniff'));

const strictTransportSecurityServerHttpHeadersWriter = (maxAgeInSeconds: number, includeSubDomains: boolean, preload: boolean) => {
    let headerValue = `max-age=${maxAgeInSeconds}`;
    if (includeSubDomains) {
        headerValue += ' ; includeSubDomains';
    }
    if (preload) {
        headerValue += ' ; preload';
    }
    const delegate = staticServerHttpHeadersWriter(
        new MapHttpHeaders()
            .add('strict-transport-security', headerValue))

    const isSecure = (exchange: ServerWebExchange) => {
        const protocol = exchange.request.URL.protocol;
        return protocol === 'https:';
    }

    return async (exchange: ServerWebExchange) => {
        if (isSecure(exchange)) {
            await delegate(exchange);
        }
    }
};

type FrameOptionsMode = 'DENY' | 'SAMEORIGIN';
const frameOptionsServerHttpHeadersWriter = (mode: FrameOptionsMode) => {
    return staticServerHttpHeadersWriter(
        new MapHttpHeaders()
            .add('x-frame-options', mode))
};

type XssProtectionHeaderValue = '0' | '1' | '1; mode=block';
const xssProtectionServerHttpHeadersWriter = (headerValue: XssProtectionHeaderValue) => staticServerHttpHeadersWriter(
    new MapHttpHeaders()
        .add('x-xss-protection', headerValue));


const permissionsPolicyServerHttpHeadersWriter = (policyDirectives?: string) => {

    const delegate = policyDirectives === undefined ? undefined : staticServerHttpHeadersWriter(
        new MapHttpHeaders()
            .add('permissions-policy', policyDirectives));
    return async (exchange: ServerWebExchange) => {
        if (delegate !== undefined) {
            await delegate(exchange);
        }
    }
}
const contentSecurityPolicyServerHttpHeadersWriter = (policyDirectives?: string, reportOnly?: boolean) => {
    const headerName = reportOnly ? 'content-security-policy-report-only' : 'content-security-policy';
    const delegate = policyDirectives === undefined ? undefined : staticServerHttpHeadersWriter(
        new MapHttpHeaders()
            .add(headerName, policyDirectives));
    return async (exchange: ServerWebExchange) => {
        if (delegate !== undefined) {
            await delegate(exchange);
        }
    }
};

type ReferrerPolicy =
    'no-referrer'
    | 'no-referrer-when-downgrade'
    | 'origin'
    | 'origin-when-cross-origin'
    | 'same-origin'
    | 'strict-origin'
    | 'strict-origin-when-cross-origin'
    | 'unsafe-url';
const refererPolicyServerHttpHeadersWriter = (policy: ReferrerPolicy = 'no-referrer') => {
    return staticServerHttpHeadersWriter(
        new MapHttpHeaders()
            .add('referer-policy', policy));
}

type CrossOriginOpenerPolicy = 'unsafe-none' | 'same-origin' | 'same-origin-allow-popups';
const crossOriginOpenerPolicyServerHttpHeadersWriter = (policy?: CrossOriginOpenerPolicy) => {
    const delegate = policy === undefined ? undefined : staticServerHttpHeadersWriter(
        new MapHttpHeaders()
            .add('cross-origin-opener-policy', policy));
    return async (exchange: ServerWebExchange) => {
        if (delegate !== undefined) {
            await delegate(exchange);
        }
    }
}

type CrossOriginEmbedderPolicy = 'unsafe-none' | 'require-corp';
const crossOriginEmbedderPolicyServerHttpHeadersWriter = (policy?: CrossOriginEmbedderPolicy) => {
    const delegate = policy === undefined ? undefined : staticServerHttpHeadersWriter(
        new MapHttpHeaders()
            .add('cross-origin-embedder-policy', policy));
    return async (exchange: ServerWebExchange) => {
        if (delegate !== undefined) {
            await delegate(exchange);
        }
    }
}

type CrossOriginResourcePolicy = 'same-origin' | 'same-site' | 'cross-origin';
const crossOriginResourcePolicyServerHttpHeadersWriter = (policy?: CrossOriginResourcePolicy) => {
    const delegate = policy === undefined ? undefined : staticServerHttpHeadersWriter(
        new MapHttpHeaders()
            .add('cross-origin-resource-policy', policy));
    return async (exchange: ServerWebExchange) => {
        if (delegate !== undefined) {
            await delegate(exchange);
        }
    }
}

const compositeServerHttpHeadersWriter = (
    ...writers: ServerHttpHeadersWriter[]
): ServerHttpHeadersWriter => {
    return async (exchange: ServerWebExchange) => {
        for (const writer of writers) {
            await writer(exchange);
        }
    }
}

export default function headers(opts?: {
    cache?: { disabled?: boolean }
    contentType?: { disabled?: boolean },
    frameOptions?: { disabled?: boolean, mode?: FrameOptionsMode },
    hsts?: { disabled?: boolean, maxAge?: number, includeSubDomains?: boolean, preload?: boolean },
    xss?: { disabled?: boolean, headerValue?: XssProtectionHeaderValue },
    permissionsPolicy?: { disabled?: boolean, policyDirectives?: string },
    contentSecurityPolicy?: { disabled?: boolean, policyDirectives?: string, reportOnly?: boolean },
    refererPolicy?: { disabled?: boolean, policy?: ReferrerPolicy },
    crossOriginOpenerPolicy?: { disabled?: boolean, policy?: CrossOriginOpenerPolicy },
    crossOriginEmbedderPolicy?: { disabled?: boolean, policy?: CrossOriginEmbedderPolicy },
    crossOriginResourcePolicy?: { disabled?: boolean, policy?: CrossOriginResourcePolicy }
    writers?: ServerHttpHeadersWriter[]
}): WebFilter {
    const writers: ServerHttpHeadersWriter[] = [];
    if (!opts?.cache?.disabled) {
        writers.push(cacheControlServerHttpHeadersWriter());
    }
    if (!opts?.contentType?.disabled) {
        writers.push(contentTypeServerHttpHeadersWriter());
    }
    if (!opts?.hsts?.disabled) {
        writers.push(strictTransportSecurityServerHttpHeadersWriter(opts?.hsts?.maxAge ?? 365 * 24 * 60 * 60, opts?.hsts?.includeSubDomains ?? true, opts?.hsts?.preload ?? false));
    }
    if (!opts?.frameOptions?.disabled) {
        writers.push(frameOptionsServerHttpHeadersWriter(opts?.frameOptions?.mode ?? 'DENY'));
    }
    if (!opts?.xss?.disabled) {
        writers.push(xssProtectionServerHttpHeadersWriter(opts?.xss?.headerValue ?? '0'));
    }
    if (!opts?.permissionsPolicy?.disabled) {
        writers.push(permissionsPolicyServerHttpHeadersWriter(opts?.permissionsPolicy?.policyDirectives));
    }
    if (!opts?.contentSecurityPolicy?.disabled) {
        writers.push(contentSecurityPolicyServerHttpHeadersWriter(opts?.contentSecurityPolicy?.policyDirectives ?? "default-src 'self'", opts?.contentSecurityPolicy?.reportOnly));
    }
    if (!opts?.refererPolicy?.disabled) {
        writers.push(refererPolicyServerHttpHeadersWriter(opts?.refererPolicy?.policy ?? 'no-referrer'));
    }
    if (!opts?.crossOriginOpenerPolicy?.disabled) {
        writers.push(crossOriginOpenerPolicyServerHttpHeadersWriter(opts?.crossOriginOpenerPolicy?.policy));
    }
    if (!opts?.crossOriginEmbedderPolicy?.disabled) {
        writers.push(crossOriginEmbedderPolicyServerHttpHeadersWriter(opts?.crossOriginEmbedderPolicy?.policy));
    }
    if (!opts?.crossOriginResourcePolicy?.disabled) {
        writers.push(crossOriginResourcePolicyServerHttpHeadersWriter(opts?.crossOriginResourcePolicy?.policy));
    }
    if (opts?.writers) {
        writers.push(...opts.writers);
    }
    const writer = compositeServerHttpHeadersWriter(...writers);

    return {
        filter: async (exchange: ServerWebExchange, chain: WebFilterChain) => {
            await writer(exchange);
            return chain.filter(exchange);
        }
    }
}
